package com.evoapps.evoshift.domain.model

import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.plus
import kotlin.math.round

/**
 * How a ledger item covering several days ([FixedCost.endDate]) falls on its days: an equal part
 * of every day from the first to the last (inclusive) in whole cents, rounded down, and the last
 * day takes the remainder, so the parts add up to the amount exactly. A single day takes all of
 * it. functions/ledgerSpread.js splits it the same way.
 */
object LedgerSpread {
    /** The ISO days from [first] to [last], inclusive; just [first] when they cannot be read. */
    fun days(first: String, last: String): List<String> {
        if (last <= first) return listOf(first)
        val start = runCatching { LocalDate.parse(first) }.getOrNull() ?: return listOf(first)
        val end = runCatching { LocalDate.parse(last) }.getOrNull() ?: return listOf(first)
        return generateSequence(start) { it.plus(DatePeriod(days = 1)) }.takeWhile { it <= end }.map { it.toString() }.toList()
    }

    /** SH-195: the first day ("YYYY-MM-01") of [iso]'s month; null when it cannot be read. */
    fun monthStart(iso: String): String? = runCatching { LocalDate.parse(iso) }.getOrNull()?.let { LocalDate(it.year, it.month, 1).toString() }

    /** SH-195: the last day of [iso]'s month; null when it cannot be read. */
    fun monthEnd(iso: String): String? = runCatching { LocalDate.parse(iso) }.getOrNull()?.let {
        val first = LocalDate(it.year, it.month, 1)
        first.plus(DatePeriod(months = 1)).plus(DatePeriod(days = -1)).toString()
    }

    /** [day]'s part of [amount] spread over [first]..[last]. */
    fun share(amount: Double, first: String, last: String, day: String): Double {
        if (last <= first) return if (day == first) amount else 0.0
        if (day < first || day > last) return 0.0
        val all = days(first, last)
        if (all.size <= 1) return if (day == first) amount else 0.0
        val totalCents = round(amount * 100).toLong()
        val dayCents = totalCents / all.size
        val cents = if (day == all.last()) totalCents - dayCents * (all.size - 1) else dayCents
        return cents / 100.0
    }
}
